import { useEffect, useMemo, useRef, useState } from "react";
import swal from "sweetalert";
import { toast } from "react-toastify";
import { useNavigate, useParams } from "react-router-dom";
import { useTranslation } from "react-i18next";
import PageTitle from "../../layouts/PageTitle";
import config from "../../../../config.json";
import { getMenuLanguages, MENU_LANGUAGE_CODES } from "../../../constants/menuLanguages";
import {
  assignProductsToSubCategory,
  fetchSubCategoryById,
  updateSubCategory,
} from "../../../services/subcategories";
import {
  createProductQuick,
  fetchProducts,
  hiddenChoiceItemsFromKeep,
  syncProductChoicesAndItems,
} from "../../../services/MenuService";
import WizardLivePhone from "../Setup/StartNowWizard/WizardLivePhone";
import {
  buildSubcategoryFormLivePreview,
  productChoiceChildren,
  productTitle,
} from "../Cms/MenuTree/menuTreeUtils";
import ProductChoiceKeepPanel from "../Cms/MenuTree/ProductChoiceKeepPanel";
import { IMAGE_PICKER_ACCEPT, validateImageFile } from "../../../utils/imageValidation";
import "../../../i18n";
import "../Cms/MenuTree/MenuTree.css";
import "./AddCategory.css";

const bannerUrl = (filename) => `${config.UPLOADS_SERVER}/category/${filename}`;

const assignedProductsOf = (sub) =>
  (sub?.productList || [])
    .filter((row) => row?.product != null && row.product.isArchived !== true)
    .sort((a, b) => (a.order || 0) - (b.order || 0))
    .map((row) => row.product)
    .filter((product) => product?._id);

/**
 * Edit subcategory: same single-page catform as add — banner, language pills,
 * orientation, products with keep/hide choices. Save updates details then replaces
 * the assigned product list.
 */
const EditSubcategory = () => {
  const { t } = useTranslation();
  const navigate = useNavigate();
  const { id } = useParams();

  const languages = useMemo(() => getMenuLanguages(t), [t]);

  const [loading, setLoading] = useState(true);
  const [notFound, setNotFound] = useState(false);

  const [titles, setTitles] = useState(() =>
    Object.fromEntries(MENU_LANGUAGE_CODES.map((code) => [code, ""]))
  );
  const [descriptions, setDescriptions] = useState(() =>
    Object.fromEntries(MENU_LANGUAGE_CODES.map((code) => [code, ""]))
  );
  const [openLangs, setOpenLangs] = useState({});
  const setTitle = (code, value) => setTitles((prev) => ({ ...prev, [code]: value }));
  const hasAnyTitle = Object.values(titles).some((v) => v?.trim());
  const isOpen = (code) => Boolean(openLangs[code]) || Boolean(titles[code]?.trim());

  const [file, setFile] = useState(null);
  const [imageUrl, setImageUrl] = useState(null);
  const [savedBanner, setSavedBanner] = useState(null);
  const [photoRemoved, setPhotoRemoved] = useState(false);
  const fileInputRef = useRef(null);
  useEffect(() => () => imageUrl && URL.revokeObjectURL(imageUrl), [imageUrl]);

  const previewSrc =
    imageUrl || (!photoRemoved && savedBanner ? bannerUrl(savedBanner) : null);

  const handleFileChange = (event) => {
    const selected = event.target.files?.[0];
    event.target.value = "";
    if (!selected) return;
    const { isValid, message } = validateImageFile(selected, 5);
    if (!isValid) {
      toast.error(message);
      return;
    }
    setFile(selected);
    setImageUrl(URL.createObjectURL(selected));
    setPhotoRemoved(false);
  };

  const [displayOrientation, setDisplayOrientation] = useState("vertical");
  const [bannerImageDisabled, setBannerImageDisabled] = useState(false);

  const [allProducts, setAllProducts] = useState([]);
  const [productsLoading, setProductsLoading] = useState(true);
  const [selectedProductIds, setSelectedProductIds] = useState([]);
  const [productSearch, setProductSearch] = useState("");
  const [newProductName, setNewProductName] = useState("");
  const [creatingProduct, setCreatingProduct] = useState(false);
  const [creatingNew, setCreatingNew] = useState(false);
  const [productsOpen, setProductsOpen] = useState(false);
  const [keptChoices, setKeptChoices] = useState({});
  const [keptItems, setKeptItems] = useState({});
  const [openProductPanels, setOpenProductPanels] = useState({});

  useEffect(() => {
    let cancelled = false;
    (async () => {
      try {
        const [data, catalogRaw] = await Promise.all([
          fetchSubCategoryById(id),
          fetchProducts().catch(() => []),
        ]);
        if (cancelled) return;
        if (!data?._id) {
          setNotFound(true);
          return;
        }

        const nextTitles = {};
        const nextDescriptions = {};
        const nextOpen = {};
        MENU_LANGUAGE_CODES.forEach((code) => {
          const langData = data.subCategoryDetails?.find((d) => d.language === code);
          nextTitles[code] = langData?.title || "";
          nextDescriptions[code] = langData?.description || "";
          if (nextTitles[code].trim()) nextOpen[code] = true;
        });
        setTitles(nextTitles);
        setDescriptions(nextDescriptions);
        setOpenLangs(nextOpen);
        setDisplayOrientation(data.displayOrientation === "horizontal" ? "horizontal" : "vertical");
        setBannerImageDisabled(data.bannerImageDisabled === true);
        setSavedBanner(
          typeof data.bannerImage === "string" && data.bannerImage.trim()
            ? data.bannerImage.trim()
            : null
        );
        setPhotoRemoved(false);
        setFile(null);
        setImageUrl(null);

        const assigned = assignedProductsOf(data);
        const assignedIds = assigned.map((p) => String(p._id)).filter(Boolean);
        const catalog = Array.isArray(catalogRaw) ? catalogRaw : [];
        const merged = [...catalog];
        assigned.forEach((product) => {
          const pid = String(product._id);
          const idx = merged.findIndex((p) => String(p._id) === pid);
          if (idx === -1) merged.unshift(product);
          else if (!merged[idx].choices?.length && product.choices?.length) merged[idx] = product;
        });
        setAllProducts(merged);
        setSelectedProductIds(assignedIds);
        setOpenProductPanels({});
        setProductsOpen(assignedIds.length > 0);
        setKeptChoices({});
        setKeptItems({});
      } catch (err) {
        console.error("EditSubcategory: load failed", err);
        toast.error(t("errorTitle"));
        setNotFound(true);
      } finally {
        if (!cancelled) {
          setLoading(false);
          setProductsLoading(false);
        }
      }
    })();
    return () => {
      cancelled = true;
    };
  }, [id, t]);

  const productsById = useMemo(
    () => Object.fromEntries(allProducts.map((p) => [String(p._id), p])),
    [allProducts]
  );
  const livePreview = useMemo(
    () =>
      buildSubcategoryFormLivePreview({
        titles,
        productsById,
        selectedProductIds,
        displayOrientation,
        bannerUrl: previewSrc,
        bannerImageDisabled,
      }),
    [titles, productsById, selectedProductIds, displayOrientation, previewSrc, bannerImageDisabled]
  );
  const query = productSearch.trim().toLowerCase();
  const visibleProducts = allProducts.filter(
    (p) => !query || productTitle(p, "").toLowerCase().includes(query)
  );

  const toggleProduct = (productId) => {
    const wasSelected = selectedProductIds.includes(productId);
    setSelectedProductIds((prev) =>
      wasSelected ? prev.filter((x) => x !== productId) : [...prev, productId]
    );
  };

  const cancelCreateProduct = () => {
    if (creatingProduct) return;
    setCreatingNew(false);
    setNewProductName("");
  };

  const handleCreateProduct = async () => {
    const name = newProductName.trim();
    if (!name || creatingProduct) return;
    setCreatingProduct(true);
    try {
      const created = await createProductQuick(name);
      if (!created?._id) throw new Error("no id");
      const productId = String(created._id);
      setAllProducts((prev) =>
        prev.some((p) => String(p._id) === productId) ? prev : [created, ...prev]
      );
      setSelectedProductIds((prev) => (prev.includes(productId) ? prev : [...prev, productId]));
      setNewProductName("");
      setProductSearch("");
      setCreatingNew(false);
      setProductsOpen(true);
    } catch (err) {
      toast.error(err?.response?.data?.message || t("subFormProductCreateFailed"));
    } finally {
      setCreatingProduct(false);
    }
  };

  const [submitting, setSubmitting] = useState(false);
  const canSubmit = hasAnyTitle && !submitting && !loading;

  const handleSubmit = async (e) => {
    e?.preventDefault?.();
    if (!canSubmit) return;

    const subCategoryDetails = languages
      .map((l) => ({
        language: l.code,
        title: titles[l.code]?.trim() || "",
        description: descriptions[l.code]?.trim() || "",
      }))
      .filter((d) => d.title);

    if (!subCategoryDetails.some((d) => d.title)) {
      swal(t("warningTitle"), t("subFormTitleRequired"), "warning");
      return;
    }

    const formData = new FormData();
    formData.append(
      "data",
      JSON.stringify({
        subCategoryDetails,
        displayOrientation,
        bannerImageDisabled,
        removeBanner: !!photoRemoved && !file && !!savedBanner,
      })
    );
    if (file) formData.append("file", file);

    setSubmitting(true);
    try {
      await updateSubCategory(id, formData);
      if (file) {
        setSavedBanner(null);
        setFile(null);
        setImageUrl(null);
        setPhotoRemoved(false);
      } else if (photoRemoved) {
        setSavedBanner(null);
        setPhotoRemoved(false);
      }

      try {
        await assignProductsToSubCategory(
          String(id),
          selectedProductIds.map((product, index) => ({ product, order: index + 1 }))
        );
        const synced = new Set();
        for (const productId of selectedProductIds) {
          if (synced.has(productId)) continue;
          const children = productChoiceChildren(productsById[productId]);
          if (!children.length) continue;
          synced.add(productId);
          const keptChoiceIds = keptChoices[productId] ?? children.map((c) => c.id);
          await syncProductChoicesAndItems(
            productId,
            keptChoiceIds,
            hiddenChoiceItemsFromKeep(children, keptChoiceIds, keptItems[productId] || {})
          );
        }
      } catch (err) {
        console.error("EditSubcategory: assign products failed", err);
        toast.warn(t("subFormUpdateAttachFailed"));
      }

      swal({
        title: t("successTitle"),
        text: t("subcategoryUpdated"),
        icon: "success",
        timer: 1000,
        buttons: false,
      });
      setTimeout(() => navigate(`/subcategory-details/${id}`), 1000);
    } catch (err) {
      console.error("EditSubcategory: save failed", err?.response?.data || err.message);
      swal(t("errorTitle"), err?.response?.data?.message || String(err), "error");
    } finally {
      setSubmitting(false);
    }
  };

  const langInput = (lang, required = false) => (
    <div className="catform__field" key={lang.code}>
      <label className="catform__label" htmlFor={`edit-sub-title-${lang.code}`}>
        <img src={lang.flag} alt="" />
        {lang.label}
        {required && <span className="catform__required">{t("catFormRequired")}</span>}
      </label>
      <input
        id={`edit-sub-title-${lang.code}`}
        type="text"
        className="catform__input"
        value={titles[lang.code] || ""}
        onChange={(e) => setTitle(lang.code, e.target.value)}
      />
    </div>
  );

  if (loading) {
    return (
      <>
        <PageTitle activeMenu={t("editSubcategory")} motherMenu={t("kitchen")} />
        <div className="catform">
          <p className="catform__empty">{t("loading")}…</p>
        </div>
      </>
    );
  }

  if (notFound) {
    return (
      <>
        <PageTitle activeMenu={t("editSubcategory")} motherMenu={t("kitchen")} />
        <div className="catform">
          <p className="catform__empty">{t("noTitle")}</p>
          <div className="catform__actions">
            <button
              type="button"
              className="btn catform__btn catform__btn--cancel"
              onClick={() => navigate("/subcategoriesList")}
            >
              {t("subFormList")}
            </button>
          </div>
        </div>
      </>
    );
  }

  return (
    <>
      <PageTitle activeMenu={t("editSubcategory")} motherMenu={t("kitchen")} />

      <div className="catform-with-preview">
      <form className="catform" onSubmit={handleSubmit} noValidate>
        <div className="catform__top">
          <h1 className="catform__title">{t("editSubcategory")}</h1>
          <button
            type="button"
            className="btn catform__link"
            onClick={() => navigate(`/subcategory-details/${id}`)}
          >
            <i className="fas fa-arrow-left" aria-hidden /> {t("viewSubcategoryDetails")}
          </button>
        </div>

        <section className="catform__section">
          <h2 className="catform__heading">
            {t("subFormBanner")} <span className="catform__optional">{t("optional")}</span>
          </h2>
          <p className="catform__help">{t("subFormBannerHelp")}</p>
          <input
            ref={fileInputRef}
            hidden
            type="file"
            accept={IMAGE_PICKER_ACCEPT}
            onChange={handleFileChange}
          />
          <div className="catform__photo">
            <button
              type="button"
              className="catform__thumb catform__thumb--banner"
              onClick={() => fileInputRef.current?.click()}
            >
              {previewSrc ? <img src={previewSrc} alt="" /> : <i className="fas fa-image" aria-hidden />}
            </button>
            <div className="catform__photo-actions">
              <button type="button" className="btn catform__small" onClick={() => fileInputRef.current?.click()}>
                {previewSrc ? t("catFormReplacePhoto") : t("subFormChooseBanner")}
              </button>
              {previewSrc && (
                <button
                  type="button"
                  className="btn catform__small is-danger"
                  onClick={() => {
                    setFile(null);
                    setImageUrl(null);
                    setPhotoRemoved(true);
                  }}
                >
                  {t("catFormRemovePhoto")}
                </button>
              )}
              {previewSrc ? (
                <label className={`catform__radio ${!bannerImageDisabled ? "is-on" : ""}`}>
                  <input
                    type="checkbox"
                    checked={!bannerImageDisabled}
                    onChange={(e) => setBannerImageDisabled(!e.target.checked)}
                  />
                  {t("subFormShowBanner")}
                </label>
              ) : null}
            </div>
          </div>
        </section>

        <section className="catform__section">
          <h2 className="catform__heading">{t("catFormStepName")}</h2>
          <p className="catform__pills-label">{t("subFormChooseLanguage")}</p>
          <div className="catform__pills">
            {languages.map((lang) => {
              const filled = Boolean(titles[lang.code]?.trim());
              return (
                <button
                  key={lang.code}
                  type="button"
                  className={`catform__pill ${isOpen(lang.code) ? "is-open" : ""} ${filled ? "is-filled" : ""}`}
                  aria-pressed={isOpen(lang.code)}
                  onClick={() => setOpenLangs((prev) => ({ ...prev, [lang.code]: !prev[lang.code] }))}
                >
                  <img src={lang.flag} alt="" />
                  {lang.label}
                  {filled && <i className="fas fa-check" aria-hidden />}
                </button>
              );
            })}
          </div>
          <div className="catform__lang-fields">
            {languages.filter((lang) => isOpen(lang.code)).map((lang) => langInput(lang))}
          </div>
        </section>

        <section className="catform__section catform__section--orient">
          <div className="catform__row">
            <h2 className="catform__heading">{t("displayOrientation")}</h2>
            <div className="catform__radios">
              {["horizontal", "vertical"].map((value) => (
                <label
                  key={value}
                  className={`catform__radio ${displayOrientation === value ? "is-on" : ""}`}
                >
                  <input
                    type="radio"
                    name="subcategoryDisplayOrientation"
                    value={value}
                    checked={displayOrientation === value}
                    onChange={() => setDisplayOrientation(value)}
                  />
                  {t(value)}
                </label>
              ))}
            </div>
          </div>
        </section>

        <section className={`catform__section ${productsOpen ? "is-open" : ""}`}>
          <button
            type="button"
            className="catform__fold"
            aria-expanded={productsOpen}
            aria-controls="editsub-products"
            onClick={() => setProductsOpen((open) => !open)}
          >
            <h2 className="catform__heading">
              {t("subFormStepProducts")} <span className="catform__optional">{t("optional")}</span>
              {selectedProductIds.length > 0 && (
                <span className="catform__fold-count">{selectedProductIds.length}</span>
              )}
            </h2>
            <i
              className={`fas fa-chevron-down catform__fold-arrow ${productsOpen ? "is-open" : ""}`}
              aria-hidden
            />
          </button>

          <p className="catform__help">{t("subFormProductsHelp")}</p>

          {productsOpen && (
            <div id="editsub-products" className="catform__fold-body">
              {selectedProductIds.length > 0 && (
                <div className="catform__chips">
                  {selectedProductIds.map((productId) => (
                    <span key={productId} className="catform__chip">
                      {productTitle(productsById[productId], t("subFormUnknownProduct"))}
                      <button type="button" aria-label={t("remove")} onClick={() => toggleProduct(productId)}>
                        <i className="fas fa-times" />
                      </button>
                    </span>
                  ))}
                </div>
              )}

              <input
                type="search"
                className="catform__input"
                placeholder={t("subFormSearchProducts")}
                value={productSearch}
                onChange={(e) => setProductSearch(e.target.value)}
              />

              <div className="catform__list">
                {productsLoading ? (
                  <div className="catform__empty">{t("loading")}…</div>
                ) : allProducts.length === 0 ? (
                  <div className="catform__empty">{t("subFormNoProducts")}</div>
                ) : visibleProducts.length === 0 ? (
                  <div className="catform__empty">{t("subFormNoMatch")}</div>
                ) : (
                  visibleProducts.map((product) => {
                    const productId = String(product._id);
                    const checked = selectedProductIds.includes(productId);
                    const choices = productChoiceChildren(product);
                    const hasPanel = checked && choices.length > 0;
                    const panelOpen = hasPanel && (openProductPanels[productId] ?? false);
                    return (
                      <div
                        key={productId}
                        className={`catform__item ${checked ? "is-checked" : ""} ${
                          panelOpen ? "is-expanded" : ""
                        }`}
                      >
                        <div className="catform__item-head">
                          <label className={`catform__option ${checked ? "is-checked" : ""}`}>
                            <input
                              type="checkbox"
                              checked={checked}
                              onChange={() => toggleProduct(productId)}
                            />
                            <span className="catform__option-name">{productTitle(product)}</span>
                            {choices.length > 0 && (
                              <span className={`menu-picker__badge ${hasPanel ? "menu-picker__badge--kept" : ""}`}>
                                {hasPanel
                                  ? t("menuTreeKeptBadge", {
                                      kept: (keptChoices[productId] ?? choices.map((c) => c.id)).length,
                                      total: choices.length,
                                    })
                                  : t("menuTreeChoicesBadge", { count: choices.length })}
                              </span>
                            )}
                          </label>
                          {hasPanel && (
                            <button
                              type="button"
                              className={`btn menu-picker__chevron ${panelOpen ? "is-open" : ""}`}
                              onClick={() =>
                                setOpenProductPanels((prev) => ({
                                  ...prev,
                                  [productId]: !(prev[productId] ?? false),
                                }))
                              }
                              aria-expanded={panelOpen}
                            >
                              <i className="fas fa-chevron-down" aria-hidden />
                            </button>
                          )}
                        </div>
                        {panelOpen && (
                          <div className="menu-picker__panel">
                            <p className="menu-picker__subtitle mb-2">{t("prodFormKeepChoicesHint")}</p>
                            <ProductChoiceKeepPanel
                              productId={productId}
                              choices={choices}
                              keptChoiceIds={keptChoices[productId]}
                              onKeptChoicesChange={(ids) =>
                                setKeptChoices((prev) => ({ ...prev, [productId]: ids }))
                              }
                              keptItems={keptItems[productId] || {}}
                              onKeptItemsChange={(choiceId, ids) =>
                                setKeptItems((prev) => ({
                                  ...prev,
                                  [productId]: { ...(prev[productId] || {}), [choiceId]: ids },
                                }))
                              }
                              t={t}
                            />
                          </div>
                        )}
                      </div>
                    );
                  })
                )}
              </div>

              <div className="catform__new">
                {creatingNew ? (
                  <div className="menu-picker__create-form">
                    <input
                      type="text"
                      className="menu-picker__create-input"
                      placeholder={t("subFormNewProductPlaceholder")}
                      aria-label={t("subFormNewProductPlaceholder")}
                      value={newProductName}
                      disabled={creatingProduct}
                      autoFocus
                      onChange={(e) => setNewProductName(e.target.value)}
                      onKeyDown={(e) => {
                        if (e.key === "Enter") {
                          e.preventDefault();
                          handleCreateProduct();
                        }
                        if (e.key === "Escape") cancelCreateProduct();
                      }}
                    />
                    <button
                      type="button"
                      className="btn menu-picker__create-submit"
                      disabled={!newProductName.trim() || creatingProduct}
                      onClick={handleCreateProduct}
                    >
                      {creatingProduct ? (
                        <span className="spinner-border spinner-border-sm" role="status" aria-hidden />
                      ) : (
                        t("menuTreeCreate")
                      )}
                    </button>
                    <button
                      type="button"
                      className="btn menu-picker__create-cancel"
                      onClick={cancelCreateProduct}
                      disabled={creatingProduct}
                      aria-label={t("cancel")}
                    >
                      <i className="fas fa-times" aria-hidden />
                    </button>
                  </div>
                ) : (
                  <button type="button" className="btn menu-picker__create" onClick={() => setCreatingNew(true)}>
                    <span className="menu-picker__create-plus" aria-hidden>
                      <i className="fas fa-plus" />
                    </span>
                    {t("menuTreeNewProduct")}
                  </button>
                )}
              </div>
            </div>
          )}
        </section>

        <div className="catform__actions">
          <button
            type="button"
            className="btn catform__btn catform__btn--cancel"
            onClick={() => navigate(`/subcategory-details/${id}`)}
            disabled={submitting}
          >
            {t("cancel")}
          </button>
          <button type="submit" className="btn catform__btn catform__btn--primary" disabled={!canSubmit}>
            {submitting ? t("loading") : t("saveChanges")}
          </button>
        </div>
      </form>

      <aside className="menu-manage-live" aria-label={t("wizardPreviewLive")}>
        <WizardLivePhone
          draft={livePreview.draft}
          productPhotos={livePreview.productPhotos}
          compact
          showStepLabel={false}
          emptyHintKey="subFormLiveEmpty"
        />
      </aside>
      </div>
    </>
  );
};

export default EditSubcategory;
